import { useEffect, useState } from "react";
import { realtimeApi, RealtimeBucket, RealtimeCountryRow } from "../../services/realtimeApi";
import type { DateRange } from "../../components/common/DateRangePicker";

export interface RealtimeCountry {
  rows: RealtimeCountryRow[] | null; // null until the first successful load
  bucket: RealtimeBucket;
  loading: boolean;
  error: string | null;
  /** The account doesn't have the realtime_data_country view (403). */
  notEnabled: boolean;
}

/**
 * Country breakdown rows for a scope over a date range. Pass `targetId` for a
 * single area (server-side filter); omit it to get every target the account
 * can see, which the project view then narrows to its members. Fetching is
 * lazy — nothing happens until `enabled` (the tab is open).
 */
export function useRealtimeCountry(opts: {
  enabled: boolean;
  targetId?: string;
  dateRange: DateRange;
}): RealtimeCountry {
  const { enabled, targetId, dateRange } = opts;
  const [rows, setRows] = useState<RealtimeCountryRow[] | null>(null);
  const [bucket, setBucket] = useState<RealtimeBucket>("raw");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notEnabled, setNotEnabled] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setNotEnabled(false);
        const data = await realtimeApi.getRealtimeDataCountry(
          10000,
          targetId,
          dateRange.from ?? undefined,
          dateRange.to ?? undefined
        );
        if (cancelled) return;
        setRows(data.rows);
        setBucket(data.bucket);
      } catch (err) {
        if (cancelled) return;
        if ((err as { status?: number }).status === 403) setNotEnabled(true);
        else setError(err instanceof Error ? err.message : "Failed to load country breakdown");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, targetId, dateRange]);

  return { rows, bucket, loading, error, notEnabled };
}
